import React, { useMemo, useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  CircularProgress,
  useTheme,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Alert,
  Grid,
  Divider,
  Paper,
  Tooltip,
  TextField,
  InputAdornment,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SearchIcon from "@mui/icons-material/Search";
// Removed system selector UI
import BusinessIcon from "@mui/icons-material/Business";
import CategoryIcon from "@mui/icons-material/Category";
import ShieldIcon from "@mui/icons-material/Shield";
import PersonIcon from "@mui/icons-material/Person";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  GET_SYSTEMS,
  GET_USERS,
  CREATE_USERS,
  DELETE_USER,
} from "../../../../../apis/settingsApis/access-apis/accessApis";
import ReusableTable from "../../../../../components/ReusableTable/ReusableTable";
import { UserTableCell } from "../../Reviews/components/UserTableCell";
import BulkUserAccessDialog from "../../shared/components/BulkUserAccessDialog";
import ConfirmSnackbar from "../../../../../components/Snackbar/ConfirmSnackbar";

const UserManagementTab = ({
  systemId = null,
  systemData = null,
  onStartReview = null,
  hasDraft = false,
  startReviewLoading = false,
}) => {
  const theme = useTheme();
  const { user } = useAuth0();
  const [searchQuery, setSearchQuery] = useState("");

  // If systemData is provided from parent, use it directly
  const selectedSystem = systemData;

  // Get current user email
  const currentUserEmail = user?.email?.toLowerCase() || "";

  // Check if current user is owner of the system
  const userIsOwner = useMemo(() => {
    if (!selectedSystem?.system_admin || !currentUserEmail) return false;
    return (
      selectedSystem.system_admin.toLowerCase().trim() ===
      currentUserEmail.trim()
    );
  }, [selectedSystem, currentUserEmail]);

  // Check if selected system is manual
  const isSelectedSystemManual = useMemo(() => {
    return (selectedSystem?.integration_type || "").toUpperCase() === "MANUAL";
  }, [selectedSystem]);

  // Query users for the selected system
  const {
    data: usersData,
    loading: loadingUsers,
    refetch: refetchUsers,
  } = useQuery(GET_USERS, {
    variables: {
      page: 1,
      limit: 1000,
      filter: {
        resource_id: selectedSystem?.id ? [selectedSystem.id] : [],
      },
    },
    skip: !selectedSystem?.id,
    fetchPolicy: "cache-and-network",
  });

  const users = useMemo(() => usersData?.getUsers?.data || [], [usersData]);

  // Filter users based on search query
  const filteredUsers = useMemo(() => {
    if (!searchQuery) return users;

    const query = searchQuery.toLowerCase();
    return users.filter((user) => {
      const name = (user.user_name || user.name || "").toLowerCase();
      const email = (user.user_email || user.email || "").toLowerCase();
      const role = (user.role || "").toLowerCase();

      return (
        name.includes(query) || email.includes(query) || role.includes(query)
      );
    });
  }, [users, searchQuery]);

  // Mutations
  const [createUsers, { loading: creatingUsers }] = useMutation(CREATE_USERS, {
    onCompleted: () => {
      refetchUsers();
      setSnackbarInfo({
        message: "Users added successfully",
        vertical: "top",
        horizontal: "right",
      });
      setAddDialogOpen(false);
      setAddDialogUsers({ users: [] });
    },
    onError: (error) => {
      setSnackbarInfo({
        message: error.message || "Failed to add users",
        vertical: "top",
        horizontal: "right",
      });
    },
  });

  const [deleteUserMutation, { loading: deletingUser }] = useMutation(
    DELETE_USER,
    {
      onCompleted: () => {
        refetchUsers();
        setSnackbarInfo({
          message: "User removed successfully",
          vertical: "top",
          horizontal: "right",
        });
        setDeleteDialogOpen(false);
        setUserToDelete(null);
      },
      onError: (error) => {
        setSnackbarInfo({
          message: error.message || "Failed to remove user",
          vertical: "top",
          horizontal: "right",
        });
      },
    }
  );

  // State for user decisions
  const [decisions] = useState({});
  const [commentsByUser] = useState({});

  // Add Users dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addDialogUsers, setAddDialogUsers] = useState({ users: [] });

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Snackbar state
  const [snackbarInfo, setSnackbarInfo] = useState(null);

  // Alert state for automated system message
  const [showAutomatedSystemAlert, setShowAutomatedSystemAlert] =
    useState(true);

  // System is controlled by parent via initialSystemId; no auto-selection here

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (userToDelete && userToDelete.id) {
      deleteUserMutation({
        variables: {
          userId: String(userToDelete.id),
        },
      });
    }
  };

  const handleAddUsers = () => {
    if (!selectedSystem?.integrated_resource_id) {
      setSnackbarInfo({
        message: "System integration resource ID not found",
        vertical: "top",
        horizontal: "right",
      });
      return;
    }

    const usersToCreate = (addDialogUsers.users || []).map((u) => ({
      name: u.name,
      email: u.email,
      role: u.role,
    }));

    createUsers({
      variables: {
        input: {
          users: usersToCreate,
          integrated_account_id: selectedSystem.integrated_resource_id,
        },
      },
    });
  };

  const columns = [
    { label: "Name", field: "user_name", width: "30%" },
    { label: "Email", field: "user_email", width: "30%" },
    { label: "Role", field: "role", width: "20%" },
    { label: "Remove", field: "delete", width: "15%", align: "center" },
  ];

  const renderCell = (row, column) => {
    if (column.field === "delete") {
      return (
        <IconButton
          size="small"
          onClick={() => handleDeleteClick(row)}
          disabled={!isSelectedSystemManual || deletingUser}
          sx={{
            color: theme.palette.error.main,
            "&:hover": {
              backgroundColor: "rgba(211, 47, 47, 0.08)",
            },
            "&.Mui-disabled": {
              color: grey[400],
            },
          }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      );
    }

    const isDisabled = !isSelectedSystemManual;

    return (
      <UserTableCell
        row={row}
        column={column}
        decisions={decisions}
        commentsByUser={commentsByUser}
        selectedUsers={[]}
        isDisabled={isDisabled}
        onDecisionChange={() => { }}
        onCommentChange={() => { }}
        onUserSelection={() => { }}
      />
    );
  };

  return (
    <Box>
      <Stack spacing={3}>
        {!selectedSystem ? (
          <Paper
            sx={{
              p: 8,
              textAlign: "center",
              borderRadius: 2,
              border: `1px solid ${grey[200]}`,
            }}
          >
            <BusinessIcon sx={{ fontSize: 64, color: grey[400], mb: 2 }} />
            <Typography variant="h5" sx={{ mb: 1, color: grey[700] }}>
              No Systems Available
            </Typography>
            <Typography variant="body2" sx={{ color: grey[600] }}>
              Add a system to start managing users
            </Typography>
          </Paper>
        ) : (
          <>
            {/* Users Table Card */}
            {selectedSystem && (
              <Card
                sx={{
                  borderRadius: "12px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  border: "1px solid #E8EAED",
                }}
                variant="outlined"
              >
                <CardHeader
                  title={
                    <Typography
                      variant="h5"
                      fontWeight="600"
                      color={theme.customColors?.text?.strong}
                    >
                      System Users
                    </Typography>
                  }
                  sx={{
                    borderBottom: "1px solid #ddd",
                    background: "#F7F7FC",
                    ".MuiCardHeader-title": {
                      fontSize: "16px",
                      fontWeight: "bold",
                    },
                    padding: "14px",
                  }}
                  action={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      {onStartReview && (
                        <Tooltip
                          title={
                            !selectedSystem?.is_review_active
                              ? "System is archived. Activate it to start a review."
                              : selectedSystem?.is_critical === undefined ||
                                selectedSystem?.is_critical === null
                                ? "Please set Critical status first before starting a review"
                                : !selectedSystem?.review_cycle ||
                                  selectedSystem?.review_cycle === null
                                  ? "Please set Review Cycle first before starting a review"
                                  : !userIsOwner
                                    ? "You are not an owner of the system"
                                    : hasDraft
                                      ? "Continue the pending review"
                                      : "Start a new access review"
                          }
                          arrow
                        >
                          <span>
                            <Button
                              variant="contained"
                              startIcon={
                                startReviewLoading ? (
                                  <CircularProgress
                                    size={16}
                                    sx={{
                                      color:
                                        theme.customColors?.button?.text ||
                                        "#FFFFFF",
                                    }}
                                  />
                                ) : (
                                  <PlayArrowIcon sx={{ fontSize: 16 }} />
                                )
                              }
                              onClick={onStartReview}
                              disabled={
                                startReviewLoading ||
                                !selectedSystem?.is_review_active ||
                                !userIsOwner ||
                                selectedSystem?.is_critical === undefined ||
                                selectedSystem?.is_critical === null ||
                                !selectedSystem?.review_cycle ||
                                selectedSystem?.review_cycle === null
                              }
                              sx={{
                                bgcolor: theme.customColors?.button?.primary,
                                color: theme.customColors?.button?.text,
                                fontWeight: 500,
                                borderRadius: "8px",
                                textTransform: "none",
                                fontSize: "14px",
                                px: 2,
                                py: 1,
                                "&:hover": {
                                  bgcolor: theme.palette._blue?.dark,
                                },
                                "&:disabled": {
                                  bgcolor:
                                    theme.customColors?.background?.light,
                                  color: theme.customColors?.text?.disabled,
                                },
                              }}
                            >
                              {startReviewLoading
                                ? "Starting..."
                                : hasDraft
                                  ? "Continue Review"
                                  : "Start Review"}
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip
                        title={
                          !isSelectedSystemManual
                            ? "This system is automated. User management is handled automatically through integration."
                            : "Add users to this system"
                        }
                        arrow
                      >
                        <span>
                          <Button
                            variant="contained"
                            startIcon={<PersonAddIcon sx={{ fontSize: 16 }} />}
                            onClick={() => setAddDialogOpen(true)}
                            disabled={!isSelectedSystemManual || creatingUsers}
                            sx={{
                              bgcolor: theme.customColors?.button?.primary,
                              color: theme.customColors?.button?.text,
                              fontWeight: 500,
                              borderRadius: "8px",
                              textTransform: "none",
                              fontSize: "14px",
                              px: 2,
                              py: 1,
                              "&:hover": { bgcolor: theme.palette._blue?.dark },
                              "&:disabled": {
                                bgcolor: theme.customColors?.background?.light,
                                color: theme.customColors?.text?.disabled,
                              },
                            }}
                          >
                            Add Users
                          </Button>
                        </span>
                      </Tooltip>
                    </Box>
                  }
                />
                <CardContent sx={{ p: 0 }}>
                  {/* Search Bar */}
                  {users.length > 0 && (
                    <Box
                      sx={{
                        p: 2,
                        pb: 1.5,
                        backgroundColor: theme.customColors?.background?.main,
                      }}
                    >
                      <TextField
                        fullWidth
                        placeholder="Search users by name, email, or role..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon
                                sx={{ color: "text.secondary", fontSize: 20 }}
                              />
                            </InputAdornment>
                          ),
                          endAdornment: searchQuery && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => setSearchQuery("")}
                                edge="end"
                              >
                                <CloseIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "white",
                          },
                        }}
                      />
                    </Box>
                  )}

                  {users.length > 0 && (
                    <Divider
                      sx={{ borderColor: theme.customColors?.border?.main }}
                    />
                  )}

                  {/* Table Content */}
                  <Box>
                    {selectedSystem &&
                      !isSelectedSystemManual &&
                      showAutomatedSystemAlert && (
                        <Box sx={{ px: 3, pt: 2, pb: 2 }}>
                          <Alert
                            severity="info"
                            onClose={() => setShowAutomatedSystemAlert(false)}
                            sx={{ borderRadius: 2 }}
                          >
                            <Typography variant="body2">
                              This is an automated system. User management is
                              handled automatically through integration. Manual
                              user addition and removal is disabled.
                            </Typography>
                          </Alert>
                        </Box>
                      )}
                    {loadingUsers ? (
                      <Box sx={{ textAlign: "center", py: 8 }}>
                        <CircularProgress />
                      </Box>
                    ) : users.length === 0 ? (
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 8,
                          px: 4,
                        }}
                      >
                        <PersonIcon
                          sx={{ fontSize: 64, color: grey[300], mb: 2 }}
                        />
                        <Typography
                          variant="h5"
                          sx={{
                            mb: 1,
                            color: grey[700],
                            fontWeight: 600,
                          }}
                        >
                          No user found
                        </Typography>
                        {isSelectedSystemManual ? (
                          <>
                            <Typography
                              variant="body2"
                              sx={{
                                mb: 3,
                                color: grey[600],
                              }}
                            >
                              Add users to grant them access to this system
                            </Typography>
                            <Button
                              variant="contained"
                              startIcon={<PersonAddIcon />}
                              onClick={() => setAddDialogOpen(true)}
                              disabled={creatingUsers}
                              sx={{
                                borderRadius: 1.5,
                                textTransform: "none",
                                fontWeight: 600,
                                boxShadow: "none",
                              }}
                            >
                              Add Your First User
                            </Button>
                          </>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              mb: 3,
                              color: grey[600],
                            }}
                          >
                            If you don't see any users, please contact
                            administrator.
                          </Typography>
                        )}
                      </Box>
                    ) : filteredUsers.length === 0 ? (
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 8,
                          px: 4,
                        }}
                      >
                        <SearchIcon
                          sx={{ fontSize: 64, color: grey[300], mb: 2 }}
                        />
                        <Typography
                          variant="h5"
                          sx={{
                            mb: 1,
                            color: grey[700],
                            fontWeight: 600,
                          }}
                        >
                          No users found
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            mb: 3,
                            color: grey[600],
                          }}
                        >
                          No users match your search criteria
                        </Typography>
                        <Button
                          variant="outlined"
                          onClick={() => setSearchQuery("")}
                          sx={{
                            borderRadius: 1.5,
                            textTransform: "none",
                            fontWeight: 600,
                          }}
                        >
                          Clear Search
                        </Button>
                      </Box>
                    ) : (
                      <Box sx={{ px: 3, py: 2 }}>
                        <ReusableTable
                          columns={columns}
                          data={filteredUsers}
                          renderCell={renderCell}
                          rowsPerPage={10}
                          sx={{
                            "& .MuiTableHead-root": {
                              bgcolor: grey[50],
                            },
                            "& .MuiTableCell-head": {
                              fontWeight: 700,
                              color: grey[700],
                              fontSize: "0.75rem",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              borderBottom: `2px solid ${grey[200]}`,
                              py: 1.5,
                              px: 2,
                            },
                            "& .MuiTableRow-root": {
                              "&:hover": {
                                bgcolor: grey[50],
                              },
                            },
                            "& .MuiTableCell-root": {
                              borderBottom: `1px solid ${grey[100]}`,
                              py: 1.5,
                              px: 2,
                            },
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </Stack>

      {/* Add Users Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => {
          setAddDialogOpen(false);
          setAddDialogUsers({ users: [] });
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
            borderBottom: `1px solid ${grey[200]}`,
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Add Users
            </Typography>
            <Typography variant="body2" sx={{ color: grey[600], mt: 0.5 }}>
              Add users to {selectedSystem?.label}
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setAddDialogOpen(false);
              setAddDialogUsers({ users: [] });
            }}
            size="small"
            sx={{
              color: grey[600],
              "&:hover": {
                bgcolor: grey[100],
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <BulkUserAccessDialog
            formData={addDialogUsers}
            setFormData={setAddDialogUsers}
            theme={theme}
            existingUsers={users.map((u) => ({
              id: u.id,
              name: u.user_name || u.name,
              email: u.user_email || u.email,
              role: u.role,
            }))}
          />
        </DialogContent>
        <DialogActions
          sx={{ px: 3, py: 2, borderTop: `1px solid ${grey[200]}` }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              setAddDialogOpen(false);
              setAddDialogUsers({ users: [] });
            }}
            sx={{
              textTransform: "none",
              borderRadius: 1.5,
              px: 3,
              color: grey[700],
              borderColor: grey[300],
              "&:hover": {
                borderColor: grey[400],
                bgcolor: grey[50],
              },
            }}
            disabled={creatingUsers}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              (addDialogUsers.users || []).length === 0 || creatingUsers
            }
            onClick={handleAddUsers}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 1.5,
              px: 3,
              boxShadow: "none",
            }}
          >
            {creatingUsers
              ? "Adding..."
              : `Add ${(addDialogUsers.users || []).length} User(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setUserToDelete(null);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Remove User
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to remove{" "}
            <strong>{userToDelete?.user_name || userToDelete?.name}</strong>{" "}
            from this system?{" "}
            <strong style={{ color: theme.palette.error.main }}>
              This action cannot be undone.
            </strong>
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{ px: 3, py: 2, borderTop: `1px solid ${grey[200]}` }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              setDeleteDialogOpen(false);
              setUserToDelete(null);
            }}
            sx={{
              textTransform: "none",
              borderRadius: 1.5,
              px: 3,
              color: grey[700],
              borderColor: grey[300],
            }}
            disabled={deletingUser}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 1.5,
              px: 3,
              boxShadow: "none",
            }}
            disabled={deletingUser}
          >
            {deletingUser ? "Removing..." : "Remove User"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </Box>
  );
};

export default UserManagementTab;
